import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Kbd,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiArrowArcLeft, PiCheck, PiTrash } from 'react-icons/pi';
import React, { useEffect, useRef } from 'react';
import { cn, isNonEmpty } from '@ais-ui/utils';
import { useDrawingContext } from '@ais-ui/react-openlayers';

interface DrawingCardProps {
  className?: string;
}

export const DrawingMenu = ({ className }: DrawingCardProps) => {
  const {
    onFinish,
    onCancel,
    onClear,
    onUndo,
    isValidDrawing,
    radius,
    geometryType,
  } = useDrawingContext();
  const finishButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && finishButtonRef.current) {
        finishButtonRef.current.click();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  if (
    !onFinish.current ||
    !onCancel.current ||
    !onClear.current ||
    !onUndo.current
  ) {
    return null;
  }

  return (
    <Card>
      <div className="flex justify-between">
        <CardHeader className="pb-0 w-64">
          <CardTitle className="text-md">Draw menu</CardTitle>

          <CardDescription>
            Use the menu to undo, clear, finish or cancel the drawing
          </CardDescription>
        </CardHeader>
        <Button
          className=""
          id="drawingcard-cancel-button"
          onClick={onCancel.current}
          variant="ghost"
          size="icon"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
            className="h-4 w-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </Button>
      </div>
      {geometryType === 'Circle' && isNonEmpty(radius) && (
        <div className="flex space-x-1 pl-6 pt-2">
          <p className="text-sm font-medium flex-none">Radius</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
            {radius / 1000} kilometers
          </p>
        </div>
      )}
      <div
        className={cn('w-fit h-fit grid grid-cols-2 gap-1 p-4 m-0', className)}
      >
        <div className="flex space-x-2 items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                id="drawingcard-undo-button"
                variant="ghost"
                size="icon"
                onClick={onUndo.current}
              >
                <PiArrowArcLeft className="h-5 w-5"></PiArrowArcLeft>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Undo</TooltipContent>
          </Tooltip>
          <Label className="">Undo</Label>
        </div>
        <div className="flex space-x-2 items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                id="drawingcard-clear-button"
                onClick={onClear.current}
                variant="ghost"
                size="icon"
              >
                <PiTrash className="h-5 w-5"></PiTrash>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Clear</TooltipContent>
          </Tooltip>
          <Label>Remove</Label>
        </div>
        <div className="flex space-x-2 items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block">
                <Button
                  ref={finishButtonRef}
                  id="drawingcard-finish-button"
                  disabled={!isValidDrawing.isValid}
                  onClick={onFinish.current}
                  variant="ghost"
                  size="icon"
                >
                  <PiCheck
                    className={cn(
                      'h-5 w-5',
                      isValidDrawing.isValid && 'text-green-600'
                    )}
                  ></PiCheck>
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent className="flex space-x-2">
              {isValidDrawing.isValid ? (
                <>
                  Finish, use <Kbd className="text-xs mx-2 p-0">Enter</Kbd>
                </>
              ) : (
                isValidDrawing.reason
              )}
            </TooltipContent>
          </Tooltip>
          <Label>Finish</Label>
        </div>
      </div>
    </Card>
  );
};
